import React, { useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message } from "antd";

import HighnoteSignature from "components/highnote-signature/highnote-signature";
import routeNames from "constants/route-names";

import { useGetProofCenterPageDataQuery } from "graphql/queries/get-proof-center-page-data.generated";
import { useGetSystemDetailsQuery } from "graphql/queries/get-system-details.generated";

import ProofCenterHeader from "../../components/proof-center-header/proof-center-header";
import { ProofCenterStatus } from "constants/field-values";
import { getPropertyAddress } from "utils/helpers";

const formatDate = (timestamp: string) => {
	const date = new Date(timestamp);
	return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const SystemDetails: React.FC = () => {
	const { propertyId, systemId } = useParams<{ propertyId: string; systemId: string }>();
	const navigate = useNavigate();

	const handlePropertyNotFound = useCallback(() => {
		message.error("Sorry, that property does not exist.");
		navigate(routeNames.HOME);
	}, [navigate]);

	const { data: propertyData } = useGetProofCenterPageDataQuery({
		skip: !propertyId,
		variables: {
			propertyId: propertyId
		},
		fetchPolicy: "network-only",
		onCompleted: data => {
			if (!data.properties_by_pk || data.properties_by_pk.length === 0) {
				handlePropertyNotFound();
			}
		},
		onError: () => {
			handlePropertyNotFound();
		}
	});

	const { data: systemDetailsData } = useGetSystemDetailsQuery({
		skip: !propertyId || !systemId,
		variables: {
			propertyId: propertyId!,
			categoryId: systemId!
		},
		fetchPolicy: "network-only"
	});

	useEffect(() => {
		if (!propertyId) {
			handlePropertyNotFound();
		}
	}, [propertyId, handlePropertyNotFound]);

	const property = propertyData?.properties_by_pk?.[0];

	const category = systemDetailsData?.proof_center_categories_by_pk;

	const handleBack = () => {
		navigate(routeNames.PROOF_CENTER.replace(":propertyId", propertyId!));
	};

	const controls = category?.proof_center_category_requirements ?? [];
	const inspectionsReports = systemDetailsData?.inspections_reports ?? [];
	return (
		<div className="system-details-container">
			<div className="system-details-page-content">
				<ProofCenterHeader
					propertyName={property?.name}
					propertyAddress={getPropertyAddress(property?.address, property?.standardized_address)}
					isDraft={property?.proof_center_status === ProofCenterStatus.draft}
				/>

				{/* Main Content Card */}
				<div className="system-details-main-card">
					<button className="system-details-back-link" onClick={handleBack}>
						<img src={`${process.env.PUBLIC_URL}/assets/images/icons/arrow-purple.svg`} />
						<span>Back</span>
					</button>

					<div className="system-details-system-header">
						<div className="system-details-system-title">
							<img src={`${process.env.PUBLIC_URL}/assets/images/icons/pc-fire-suppression-alt.svg`} />
							<h2>{category?.name}</h2>
						</div>
						<a href="#" className="system-details-documentation-link">
							Request Full Documentation Package
						</a>
					</div>

					<div className="system-details-sections-container">
						<div className="system-details-section">
							<div className="system-details-section-title">Controls</div>
							<div className="system-details-section-list">
								{controls.map(control => {
									const isCompleted = control.proof_center_property_category_requirements?.[0]?.is_completed ?? false;
									return (
										<div key={control.id} className={`system-details-section-list-item system-details-control-list`}>
											<div
												className={`system-details-control-checkbox ${isCompleted ? "system-details-completed" : ""}`}
											>
												{isCompleted && (
													<svg viewBox="0 0 12 12" fill="none">
														<path
															d="M2 6L5 9L10 3"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												)}
											</div>
											<div className="system-details-control-content">
												<div className="system-details-control-name">{control.name}</div>
												{control.description && (
													<div className="system-details-control-description">{control.description}</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>

						{category?.importance && (
							<div className="system-details-section">
								<div className="system-details-section-title">Why this matters</div>
								<div className="system-details-why-matters-text">{category.importance}</div>
							</div>
						)}

						{inspectionsReports.length > 0 && (
							<div className="system-details-section">
								<div className="system-details-section-title">Inspections & Reports</div>
								<div className="system-details-section-list">
									{inspectionsReports.map(item => (
										<div key={item.id} className="system-details-section-list-item">
											<div className="system-details-inspection-title">{item.name}</div>
											<span className="system-details-section-date">{formatDate(item.created_at)}</span>
											{item.description && <p className="system-details-inspection-description">{item.description}</p>}
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="system-details-footer">
					<HighnoteSignature />
				</div>
			</div>
		</div>
	);
};

export default SystemDetails;
